import React from 'react';
import axios from 'axios';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { is_ad_user, permission_list } from '../userData.js';
import { microphoneController } from '../rpiClient/AudioController.js';
import { printerController } from '../rpiClient/PrinterController.js';
import { settingsController } from '../SettingsController';
import { poll_task } from '../connectUtils.js';
import { logEvent } from '../log';
import { errorKind } from '../log/clientEventSchema';
import { rdpFailureContext, rdpLaunchFailure } from './rdpLaunchFailure';
import { newId } from '../log/correlation';


const connection_steps = observable.box([], { deep: false });

const add_connection_step = text => {

  connection_steps.set([
    ...connection_steps.get(),
    [text, 'loading']
  ]);

};

const change_connection_step = status => {

  connection_steps.set([
    ...connection_steps.get().slice(0, -1),
    [connection_steps.get().slice(-1)[0][0], status]
  ]);

};


export const RDPModal = observer(() => (

  <div className="modal fade" id="use-rdp" tabIndex="-1" role="dialog" aria-labelledby="use-rdp" aria-hidden="true">
    <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title inner-modal-dashboard">
            <i className="fa fa-television" aria-hidden="true" style={{ paddingRight: '5px' }}></i>Use RDP
          </h5>
          <button type="button" className="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="the-content-inner text-dark">
            <div className="row ">
              <div className="col-md-12">

                {window.isElectron ?

                  <ul style={{ listStyle: 'none' }}>

                    {connection_steps.get().map(([text, status]) =>

                      <li style={{ padding: '10px' }} key={text + status}>
                        {
                          status === 'pass' &&
                          <i className="fa fa-check-circle" style={{ color: 'green', marginRight: '10px', fontSize: '1.25em', verticalAlign: 'middle' }}></i>
                        }

                        {
                          status === 'fail' &&
                          <i className="fa fa-exclamation-triangle" style={{ color: 'red', marginRight: '10px', fontSize: '1.25em', verticalAlign: 'middle' }}></i>
                        }

                        {
                          status === 'loading' &&
                          <i className="fa fa-spinner fa-pulse" style={{ color: '#AAAAAA', marginRight: '15px' }}></i>
                        }

                        {text}
                      </li>

                    )}

                  </ul>

                  :
                  <p>
                    RDP is only available in the desktop version.&nbsp;&nbsp;
                    { /* <button style={{
                              background: '#00438b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              padding: '7px'
                            }}
                              onClick={() => {}}><i
                              className="fa fa-desktop"
                              aria-hidden="true" style={{verticalAlign: 'middle'}}></i> Open in Desktop</button> */ }
                  </p>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

));



export const open_rdp = permId => {

  if (window.$('#use-rdp').is(':visible')) {
    console.log("Aborting open_rdp(). Modal still visible.")

    return
  }

  window.$('#use-rdp').modal('show')
  
  // One id for every request of this launch, so Tokyo's lines for it can be found together.
  const operation_id = newId();
  window.isElectron && _open_rdp(permId, operation_id).catch(error => report_rdp_failure(permId, error, operation_id))

}

const report_rdp_failure = (permId, error, operation_id) => logEvent(
  'connection.launch.failed',
  { ...rdpLaunchFailure({ permId, error, steps: connection_steps.get(), isRPi: !!window.isRPi }), operation_id },
);

const _open_rdp = async (permId, operation_id) => {

  const headers = { 'X-Operation-ID': operation_id };

  connection_steps.set([]);

  add_connection_step('Fetching V2 connection configuration');


  let rdp_params;
  let task_id;

  const _openUDPforwarder = async (tunnelKey, localIP, localPort) => {
    if (!settingsController.getSettings().useUdpRelay) return null
    if (!rdp_params.udp_relay_token) return null
    console.log('[UDP] relay params — host:', rdp_params.udp_relay_host, 'port:', rdp_params.udp_relay_port)
    try {
      const result = await window.api.openUDPforwarder({
        tunnelKey,
        localIP,
        localPort,
        relayHost: rdp_params.udp_relay_host,
        relayPort: rdp_params.udp_relay_port,
        bindFrameB64: rdp_params.udp_relay_token,
      })
      console.log('[UDP] forwarder opened — localPort:', result.localPort)
      return result
    } catch (err) {
      // RDP continues over SSH only; the relay failure is still worth knowing.
      logEvent('connection.launch.failed', {
        connection_type: 'udp_relay',
        step: 'open_udp_forwarder',
        ...rdpFailureContext(permId, operation_id),
        error_code: typeof err?.code === 'string' ? err.code.toLowerCase() : errorKind(err),
      })
      return null
    }
  }

  const pl = permission_list.get();
  const perm = pl[permId];

  const isWindows = window.api.platform() === 'win32';

  if (!isWindows) {

    try {

      if (is_ad_user.get()) {

        let q;

        if (perm.type === 'vmpool' || perm.type === 'vmpool-app') {
          q = `/api/advmpoolaccesss/${perm.id}/ssh_rdp_params`;
        } else {
          q = `/api/advmaccesss/${perm.id}/ssh_rdp_params`;
        }

        const data = (await axios({
          url: q,
          headers,
          params: {
            ...(pl[permId].app && { app: pl[permId].app.id })
          }
        })).data;

        rdp_params = data;
        task_id = data.task && data.task.id;

        perm.last_vmpool_vm_id = data.vm;
        perm.last_vmpool_vmuser_id = data.vmuser;
        permission_list.set(pl);

      } else {

        const data = (await axios({
          url: `/api/permissions/${permId}/ssh_rdp_params`,
          headers,
          params: {
          }
        })).data;

        rdp_params = data;
        task_id = data.task && data.task.id;

        perm.last_vmpool_vm_id = data.vm;
        perm.last_vmpool_vmuser_id = data.vmuser;
        permission_list.set(pl);
  
      }

      if (!rdp_params.vm)
        throw new Error("Invalid VM ID", rdp_params.vm)

    } catch (e) {

      change_connection_step('fail');
      throw e;

    }

    change_connection_step('pass');


    if (task_id) {

      add_connection_step('Creating VM pool system user');

      try {

        await poll_task(task_id, headers);
        change_connection_step('pass');

      } catch (e) {

        change_connection_step('fail');
        throw e;

      }
 
    }
   
  } else {   

    const { preference_values } = window.api.get_preference_fields();

    const useNetworkIP = settingsController.getSettings().useNetworkIP;
    const localIP = useNetworkIP ? await window.api.getNetworkIP() : '127.0.0.1';

    const _closeModalAndStop = () => {
      setTimeout(() => window.$('#use-rdp').modal('hide'), 500);
      window.api.set_RDP_is_starting(false);
    };

    let withPrefUrl;
    if (is_ad_user.get()) {
      if (perm.type === 'vmpool' || perm.type === 'vmpool-app') {
        withPrefUrl = `/api/advmpoolaccesss/${perm.id}/signed_rdp_params/`;
      } else {
        withPrefUrl = `/api/advmaccesss/${perm.id}/signed_rdp_params/`;
      }
      if (pl[permId].app) {
        withPrefUrl += `?app=${pl[permId].app.id}`;
      }
    } else {
      withPrefUrl = `/api/permissions/${permId}/signed_rdp_params/`;
    }

    try {
      const firstResponse = await axios({
        headers,
        method: 'post',
        url: withPrefUrl,
        data: { preference_values, local_ip: localIP }
      });
      rdp_params = firstResponse.data;
      perm.last_vmpool_vm_id = rdp_params.vm;
      perm.last_vmpool_vmuser_id = rdp_params.vmuser;
      permission_list.set(pl);
      if (!rdp_params.vm)
        throw new Error("Invalid VM ID", rdp_params.vm);
      change_connection_step('pass');
    } catch (e) {
      change_connection_step('fail');
      _closeModalAndStop();
      throw e;
    }

    if (rdp_params.task) {
      add_connection_step('Creating VM pool system user');
      try {
        await poll_task(rdp_params.task.id, headers);
        change_connection_step('pass');
      } catch (e) {
        change_connection_step('fail');
        _closeModalAndStop();
        throw e;
      }
    }

    let signedRdpContent = rdp_params.signed_rdp_content;
    let rdpTunnelPort = rdp_params.rdp_tunnel_port;
    const cachedPorts = rdp_params.cached_ports ?? [];
    const requireUdp = settingsController.getSettings().useUdpRelay;

    const _resignWithPort = async (port) => {
      const resp = await axios({ method: 'post', url: withPrefUrl, headers, data: { preference_values, suggested_port: port, local_ip: localIP } });
      signedRdpContent = resp.data.signed_rdp_content;
      rdpTunnelPort = resp.data.rdp_tunnel_port ?? port;
    };

    const _findFreePort = async () => {
      for (const p of cachedPorts) {
        if (await window.api.checkPortAvailable(p, { requireUdp, host: localIP })) {
          return { port: p, release: async () => {} };
        }
      }
      return window.api.findAvailablePortAndHold({ requireUdp, host: localIP });
    };

    if (signedRdpContent && rdpTunnelPort) {
      add_connection_step('Checking port availability');
      try {
        const portAvailable = await window.api.checkPortAvailable(rdpTunnelPort, { requireUdp, host: localIP });
        if (!portAvailable) {
          const { port: newPort, release } = await _findFreePort();
          try {
            await _resignWithPort(newPort);
          } finally {
            await release();
          }
        }
        change_connection_step('pass');
      } catch (e) {
        change_connection_step('fail');
        _closeModalAndStop();
        throw e;
      }
    }

    add_connection_step('Opening SSH tunnel');
    let windowsRdpLocalPort;
    try {
      for (let attempt = 0; ; attempt++) {
        try {
          windowsRdpLocalPort = await window.api.openForwardSSHtunnel({
            tunnelKey: `rdp${permId}_${rdp_params.vm}`,
            publicDns: rdp_params.public_dns,
            sshPort: rdp_params.ssh_port,
            sshUsername: rdp_params.ssh_username,
            sshUserPassword: rdp_params.ssh_user_password,
            remoteIP: rdp_params.private_ip,
            remotePort: rdp_params.rdp_port,
            localIPOverride: localIP,
            preferredPorts: rdpTunnelPort ? [rdpTunnelPort] : [],
            requireUdp,
          });
          break;
        } catch (e) {
          if (e.code !== 'PORT_IN_USE' || attempt >= 2 || !rdpTunnelPort) throw e;
          const { port: newPort, release } = await _findFreePort();
          try {
            await _resignWithPort(newPort);
          } finally {
            await release();
          }
        }
      }
      change_connection_step('pass');
    } catch (e) {
      change_connection_step('fail');
      _closeModalAndStop();
      throw e;
    }

    await _openUDPforwarder(`rdp${permId}_${rdp_params.vm}`, localIP, windowsRdpLocalPort)

    try {
      if (signedRdpContent) {
        const contentWithUsername = signedRdpContent
          + `username:s:${rdp_params.os_username}\r\n`
          + (rdp_params.os_domain && rdp_params.os_domain !== 'AzureAD'
              ? `domain:s:${rdp_params.os_domain}\r\n`
              : '');

        await window.api.open_rdp_from_content({
          signed_content: contentWithUsername,
          os_password: rdp_params.os_password,
          type: rdp_params.type,
          add_connection_step,
          change_connection_step,
          useMultimon: settingsController.getSettings().useMultimon,
          useMultimonSelection: settingsController.getSettings().useMultimonSelection,
          multimonSelected: settingsController.getSettings().multimonSelected,
          extra_rdp_content: rdp_params.extra_rdp_content,
          failure_context: rdpFailureContext(permId, operation_id),
        });
      } else {
        await window.api.open_rdp({
          ...rdp_params,
          local_ip: localIP,
          local_port: windowsRdpLocalPort,
          add_connection_step,
          change_connection_step,
          useMultimon: settingsController.getSettings().useMultimon,
          useMultimonSelection: settingsController.getSettings().useMultimonSelection,
          multimonSelected: settingsController.getSettings().multimonSelected,
          failure_context: rdpFailureContext(permId, operation_id),
        });
      }
    } catch (e) {
      report_rdp_failure(permId, e, operation_id);
    }

    _closeModalAndStop();
    return;

  }

  let freeRDPoptions
  if (window.isRPi) {

    // fake check speaker to let user know they selected sound redirection
    if(settingsController.getSettings().redirectSound){
      add_connection_step('Checking speaker before redirection');
      change_connection_step('pass');
    }

    // check microphone
    let isMicrophoneReady;
    if (settingsController.getSettings().redirectMicrophone) {
      add_connection_step('Checking microphone before redirection');
      // skip check microphone in new RPi OS
      window.api.isNewOS || await microphoneController.scan();// rescan and try to select available microphone
      isMicrophoneReady = window.api.isNewOS || !!microphoneController.selectedDevice;
      isMicrophoneReady ? change_connection_step('pass') : change_connection_step('fail');
    }

    // rescan available printer to eliminate unavailable printers in saved printers
    (settingsController.getSettings().printers.length > 0) && await printerController.scan();

    // build options
    freeRDPoptions = {
      screen: settingsController.getSettings().useMultimon
                ? '/multimon:force'
                : '/f',
      sound: settingsController.getSettings().redirectSound
                ? `/sound:${window.api.isNewOS ? '' : 'sys:alsa,format:1,'}quality:medium,latency:200`
                : '' ,
      mic: settingsController.getSettings().redirectMicrophone && isMicrophoneReady
                ? `/microphone${window.api.isNewOS ? '' : ':sys:alsa,format:1'}`
                : '',
      // Printer names go as a list: the desktop side quotes each one for the shell.
      printers: settingsController.getSettings().redirectAllPrinters ? '/printer' : '',
      printerNames: settingsController.getSettings().redirectAllPrinters ? [] : [...printerController.selectedList],
      predefined: `/network:auto /compression /gfx:rfx /gdi:hw /video /cert:ignore ${!!(rdp_params.os_password) ? "" : "/sec:rdp"}`
    }

  }

  const rdpTunnelKey = `rdp${permId}_${rdp_params.vm}`
  const requireUdp = settingsController.getSettings().useUdpRelay;
  const _createRDPtunnel = () => window.api.openForwardSSHtunnel({
    "tunnelKey": rdpTunnelKey,
    "sshUserPassword": rdp_params.ssh_user_password,
    "sshUsername": rdp_params.ssh_username,
    "publicDns": rdp_params.public_dns,
    "sshPort": rdp_params.ssh_port,
    "remoteIP": rdp_params.private_ip,
    "remotePort": rdp_params.rdp_port,
    "localIP": "127.0.0.1",
    "useNetworkIP": settingsController.getSettings().useNetworkIP,
    requireUdp
  })

  add_connection_step('Opening secure tunnel')
  const rdpTunnels = window.api.rdpTunnels()
  let rdpTunnel = rdpTunnels[rdpTunnelKey]
  try {
    if (!rdpTunnel) {
      console.log("Create RDP tunnel", rdpTunnelKey)
      await _createRDPtunnel()
    }

    rdpTunnel = (window.api.rdpTunnels())[rdpTunnelKey]
  } catch (e) {
    change_connection_step('fail')
    window.api.set_RDP_is_starting(false) // finished starting RDP (failed)
    throw e
  }

  change_connection_step('pass')

  try {

    if (window.isRPi) {

      add_connection_step('Launching RDP')

      const started = await window.api.openFreeRDP({
        "requires": {
          ...rdp_params,
          
          local_ip: rdpTunnel.localIP,
          local_port: rdpTunnel.localPort
        }, 
        "options": freeRDPoptions,
        "failure_context": rdpFailureContext(permId, operation_id),
      })
      // openFreeRDP returns false when it failed; the step used to show "pass" anyway.
      if (started === false) throw Object.assign(new Error('FreeRDP did not start'), { code: 'freerdp_failed' });

      // clean up previous use RDP
      Object.keys(rdpTunnels).filter(tk => tk !== rdpTunnelKey).forEach(previousTk => {
        console.log("Closing previous rdp tunnel ", previousTk)
        window.api.closeForwardSSHtunnel(previousTk)
      })

      // wait few seconds for rdp open
      await new Promise(resolve => setTimeout(resolve, 2000))
      change_connection_step('pass')

    } else {

      await _openUDPforwarder(rdpTunnelKey, rdpTunnel.localIP, rdpTunnel.localPort)

      // Check if current OS is macOS and handle Azure AD username format
      const isMacOS = window.api.platform() === 'darwin';
      const isAzureADDomain = rdp_params.os_domain === 'AzureAD';
      const hasDotBackslashPrefix = rdp_params.os_username && rdp_params.os_username.startsWith('.\\');
      if (isMacOS && isAzureADDomain && hasDotBackslashPrefix) {
        // Remove the '.\\' prefix for Azure AD accounts on macOS
        rdp_params.os_username = rdp_params.os_username.substring(2);
      }

      await window.api.open_rdp({
        ...rdp_params,
        local_ip: rdpTunnel.localIP,
        local_port: rdpTunnel.localPort,
        add_connection_step,
        change_connection_step,
        useMultimon: settingsController.getSettings().useMultimon,
        useMultimonSelection: settingsController.getSettings().useMultimonSelection,
        multimonSelected: settingsController.getSettings().multimonSelected,
        failure_context: rdpFailureContext(permId, operation_id),
      });

    }

  } catch (e) {

    report_rdp_failure(permId, e, operation_id);

  }

  setTimeout(() => window.$('#use-rdp').modal('hide'), 500)
  window.api.set_RDP_is_starting(false) // finished starting RDP

};
